// AiModeContext — global "AI mode / 專業模式" toggle.
//
// Spec: docs/specs/ai-mode-operations-console.md.
//
// AM-0 plumbing. Stores the operator's preference in
// `nexus_control.user_ai_preferences` under the `ai_mode` key (read via
// /v1/ai/preferences, write via PUT /v1/ai/preferences). New users
// default to "ai" mode; the toggle in Header writes through this
// context.
//
// Consumers:
//   AppLayout    → conditionally renders ChatBubble (hide in AI mode)
//                  + picks Sidebar variant
//   Sidebar      → renders Works list (AI mode) or NAV_GROUPS (pro)
//   Header       → renders the mode toggle UI
//   AI 導覽 page  → embeds chat panel (AI mode) or stays read-only (pro)
//
// Fail-soft: if the preference read fails (network / first visit), the
// provider falls back to "ai" mode after a short loading state. The
// loading state itself renders the previously-selected mode from
// localStorage so the page doesn't flash sidebar variants on each
// reload.

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import {
  getAiPreferences, setAiPreference,
  type AiModeValue,
} from "./lib/chatApi.js";

const STORAGE_KEY = "location.dashboard.ai_mode";

interface AiModeContextValue {
  /** Current effective mode. */
  mode: AiModeValue;
  /** Server-loaded vs local-fallback. */
  loading: boolean;
  /** Error string from the most recent set attempt; null on success. */
  error: string | null;
  /** Optimistically switch + persist. */
  setMode: (next: AiModeValue) => Promise<void>;
}

const Ctx = createContext<AiModeContextValue | null>(null);

function readLocal(): AiModeValue | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === "ai" || v === "professional") return v;
  return null;
}

function writeLocal(mode: AiModeValue): void {
  try { window.localStorage.setItem(STORAGE_KEY, mode); }
  catch { /* private mode / quota — silent */ }
}

export function AiModeProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  // Initial: previous local cache to avoid sidebar/chat flash on reload.
  // First-ever visit defaults to "ai".
  const initial = readLocal() ?? "ai";
  const [mode, setModeState] = useState<AiModeValue>(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // On mount: hydrate from server. If it differs from local cache,
  // update silently (no transition animation needed; React reconciles).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prefs = await getAiPreferences();
        if (cancelled) return;
        // Only adopt the server value when it's explicitly set —
        // undefined/missing means "operator never expressed a
        // preference", so respect the local cache (which may itself
        // be a default-AI for first-time visitors). This prevents the
        // hydration step from overriding the user's just-clicked
        // toggle when the prior server PUT failed silently. Spec §4.5.
        const server = prefs.ai_mode;
        if (server === "ai" || server === "professional") {
          setModeState(server);
          writeLocal(server);
        }
      } catch {
        // Network error / first call — keep local cache. No error
        // surfaced; the user will just see whichever variant we had.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setMode = useCallback(async (next: AiModeValue) => {
    // Optimistic local update — UI flips instantly.
    setModeState(next);
    writeLocal(next);
    setError(null);
    try {
      await setAiPreference("ai_mode", next);
    } catch (e) {
      // Server write failed (e.g. 401 because ai-api forgot attachActor
      // on /v1/ai/preferences, or 4xx for any other reason). We do NOT
      // rollback — the operator's intent stays applied locally + via
      // localStorage so reload survives. The toggle surfaces an error
      // tooltip via `error` so the user knows the cloud preference
      // failed to sync. Background workers can retry on next attempt.
      // Spec docs/specs/ai-mode-operations-console.md §4.5.
      const msg = e instanceof Error ? e.message : String(e);
      setError(`本次已切換到「${next === "ai" ? "AI 模式" : "專業模式"}」，但雲端偏好同步失敗（${msg}）— 下次登入可能回到舊設定。`);
    }
  }, []);

  const value = useMemo<AiModeContextValue>(
    () => ({ mode, loading, error, setMode }),
    [mode, loading, error, setMode],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Read the current AI mode + setter. Throws if used outside the
 *  provider — every consumer should be inside AppLayout. */
export function useAiMode(): AiModeContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAiMode must be used inside <AiModeProvider>");
  return v;
}
